import { useStore } from "./index";
import { useShallow } from "zustand/shallow";
import type { UnreadStatus, ChannelEvent, WorkSession, Member } from "./types";

// ── Auth selectors ──────────────────────────────────────────────────────────

export const useAuthReady = () => useStore((s) => s.auth.ready);
export const useAuthenticated = () => useStore((s) => !!s.auth.token && !!s.auth.user);
export const useToken = () => useStore((s) => s.auth.token);
export const useUser = () => useStore((s) => s.auth.user);
export const useIsNewLogin = () => useStore((s) => s.auth.isNewLogin);

// ── Channel selectors ───────────────────────────────────────────────────────

export const useChannelList = () =>
  useStore(
    useShallow((s) => s.channels.orderedIds.map((id) => s.channels.byId[id]))
  );

export const useChannelsLoading = () => useStore((s) => s.channels.loading);

export const useSelectedChannelId = () => useStore((s) => s.ui.selectedChannelId);

export const useSelectedChannel = () =>
  useStore((s) =>
    s.ui.selectedChannelId ? s.channels.byId[s.ui.selectedChannelId] ?? null : null
  );

// ── Message selectors ───────────────────────────────────────────────────────

export function useChannelMessages(channelId: string | null): ChannelEvent[] {
  return useStore(
    useShallow((s) => {
      if (!channelId) return EMPTY_EVENTS;
      const bucket = s.messages.byChannel[channelId];
      if (!bucket || bucket.eventIds.length === 0) return EMPTY_EVENTS;
      return bucket.eventIds.map((id) => bucket.eventsById[id]);
    })
  );
}

export function useMessagesLoading(channelId: string | null): boolean {
  return useStore((s) => {
    if (!channelId) return false;
    return s.messages.byChannel[channelId]?.loading ?? false;
  });
}

export function useMessagesFetchId(channelId: string | null): number {
  return useStore((s) => {
    if (!channelId) return 0;
    return s.messages.byChannel[channelId]?.fetchId ?? 0;
  });
}

export function useHasMoreMessages(channelId: string | null): boolean {
  return useStore((s) => {
    if (!channelId) return false;
    return s.messages.byChannel[channelId]?.hasMore ?? true;
  });
}

export function useOldestMessageCursor(channelId: string | null): string | null {
  return useStore((s) => {
    if (!channelId) return null;
    return s.messages.byChannel[channelId]?.oldestCursor ?? null;
  });
}

// ── Work selectors ──────────────────────────────────────────────────────────

export function useWorkSession(sessionId: string): WorkSession | null {
  return useStore((s) => s.work.bySessionId[sessionId] ?? null);
}

// Module-level cache so the selector returns a stable reference
let _cachedBySessionId: Record<string, WorkSession> | null = null;
let _cachedWorkMap: Map<string, WorkSession> = new Map();

export function useWorkSessionsMap(): Map<string, WorkSession> {
  return useStore((s) => {
    const byId = s.work.bySessionId;
    if (byId === _cachedBySessionId) return _cachedWorkMap;
    const entries = Object.entries(byId);
    if (entries.length === 0) {
      _cachedBySessionId = byId;
      _cachedWorkMap = EMPTY_WORK_MAP;
      return EMPTY_WORK_MAP;
    }
    _cachedBySessionId = byId;
    _cachedWorkMap = new Map(entries);
    return _cachedWorkMap;
  });
}

export function useActiveWorkSessions(channelId: string | null): WorkSession[] {
  return useStore(
    useShallow((s) => {
      if (!channelId) return EMPTY_SESSIONS;
      const sessions = Object.values(s.work.bySessionId).filter(
        (w) => w.channelId === channelId && !w.completedAt && !w.stale
      );
      return sessions.length === 0 ? EMPTY_SESSIONS : sessions;
    })
  );
}

// ── Member selectors ────────────────────────────────────────────────────────

export function useChannelMembers(channelId: string | null): Member[] {
  return useStore(
    useShallow((s) => {
      if (!channelId) return EMPTY_MEMBERS;
      return s.members.byChannel[channelId]?.list ?? EMPTY_MEMBERS;
    })
  );
}

export function useDisplayNames(channelId: string | null): {
  userDisplayNames: Map<string, string>;
  agentDisplayNames: Map<string, string>;
} {
  return useStore(
    useShallow((s) => {
      if (!channelId) return EMPTY_DISPLAY_NAMES;
      const bucket = s.members.byChannel[channelId];
      if (!bucket || bucket.list.length === 0) return EMPTY_DISPLAY_NAMES;
      return {
        userDisplayNames: bucket.userDisplayNames,
        agentDisplayNames: bucket.agentDisplayNames,
      };
    })
  );
}

// ── Agent selectors ─────────────────────────────────────────────────────────

export const useAgentList = () =>
  useStore(
    useShallow((s) => s.agents.orderedIds.map((id) => s.agents.byId[id]))
  );

export const useAgentsLoading = () => useStore((s) => s.agents.loading);

// ── Unread selectors ────────────────────────────────────────────────────────

export function useUnreadStatus(channelId: string): UnreadStatus {
  return useStore((s) => {
    const u = s.unreads.byChannel[channelId];
    if (!u || !u.lastEventId) return "none";
    if (u.lastEventId === u.lastReadEventId) return "none";
    if (u.lastEventType === "work" && u.workActive) return "working";
    return "unread";
  });
}

export function useHasAnyUnreads(): UnreadStatus {
  return useStore((s) => {
    let hasWorking = false;
    let hasUnread = false;
    for (const u of Object.values(s.unreads.byChannel)) {
      if (!u.lastEventId || u.lastEventId === u.lastReadEventId) continue;
      if (u.lastEventType === "work" && u.workActive) {
        hasWorking = true;
      } else {
        hasUnread = true;
      }
    }
    if (hasUnread) return "unread";
    if (hasWorking) return "working";
    return "none";
  });
}

export function useLastReadEventId(channelId: string | null): string | null {
  return useStore((s) => {
    if (!channelId) return null;
    return s.unreads.byChannel[channelId]?.lastReadEventId ?? null;
  });
}

// ── UI selectors ────────────────────────────────────────────────────────────

export const useSidebarCollapsed = () => useStore((s) => s.ui.sidebarCollapsed);
export const useMembersPanelOpen = () => useStore((s) => s.ui.membersPanelOpen);
export const useIsMobileStore = () => useStore((s) => s.ui.isMobile);
export const useWsStatus = () => useStore((s) => s.ui.wsStatus);

// ── Stable empty references (prevent unnecessary re-renders) ────────────────

const EMPTY_EVENTS: ChannelEvent[] = [];
const EMPTY_SESSIONS: WorkSession[] = [];
const EMPTY_MEMBERS: Member[] = [];
const EMPTY_WORK_MAP = new Map<string, WorkSession>();
const EMPTY_DISPLAY_NAMES = {
  userDisplayNames: new Map<string, string>(),
  agentDisplayNames: new Map<string, string>(),
};
